import { useEffect, useState } from 'react';

import CreateEditSchemaModalContext, {
  ModalStep,
} from '@atom/components/common/createEditSchemaModal/CreateEditSchemaModalContext';
import { Button, Icon, Modal } from '@atom/mui';
import colors from '@atom/styles/colors';
import {
  LocationType,
  SchemaBudgetType,
  SchemaSavePayload,
  SchemaTree,
  StartEndReadingOption,
  StockBasedOption,
} from '@atom/types/schema';

import BasicDetailsTab from './BasicDetailsTab';
import CostAndUnitTab from './CostAndUnitTab';
import CostTrackingTab from './CostTrackingTab';
import {
  getModalStepsArray,
  getStepIndex,
  isCurrentStepDisabled,
} from './createEditSchemaUtilities';
import StepperSidebar from './StepperSidebar';

import classes from './createEditSchemaModal.module.css';

const styles = {
  modalContent: { padding: 0 },
  cancelButton: { marginRight: '0.5rem' },
};

type CreateEditSchemaModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: (payload: SchemaSavePayload) => void;
  schema?: Partial<SchemaTree>;
};

const CreateEditSchemaModal = ({
  open,
  onClose,
  onSave,
  schema,
}: CreateEditSchemaModalProps) => {
  const [activeStep, setActiveStep] = useState<ModalStep>(
    ModalStep.BASIC_DETAILS,
  );
  const [name, setName] = useState<string>(schema?.name ?? '');
  const [locationType, setLocationType] = useState<LocationType | null>(
    schema?.locationType ?? null,
  );
  const [colorId, setColorId] = useState<number>(schema?.colorId ?? 0);
  const [markerId, setMarkerId] = useState<number>(schema?.markerId ?? 0);
  const [isMaterial, setIsMaterial] = useState<boolean>(
    schema?.isMaterial ?? false,
  );
  const [stockBasedOption, setStockBasedOption] = useState<StockBasedOption>(
    schema?.budget?.stockBasedOption ?? StockBasedOption.NONE,
  );
  const [startEndReadingOption, setStartEndReadingOption] =
    useState<StartEndReadingOption>(
      schema?.budget?.startEndReadingOption ?? StartEndReadingOption.NONE,
    );
  const [costType, setCostType] = useState<SchemaBudgetType>(
    schema?.budget?.type ?? SchemaBudgetType.VARIABLE,
  );
  const [rate, setRate] = useState<number | null>(schema?.budget?.rate ?? null);
  const [unit, setUnit] = useState<string>(schema?.budget?.unit ?? '');

  const STEPS_ARRAY = getModalStepsArray(isMaterial);

  const resetState = () => {
    setActiveStep(ModalStep.BASIC_DETAILS);
    setName(schema?.name ?? '');
    setLocationType(schema?.locationType ?? null);
    setColorId(schema?.colorId ?? 0);
    setMarkerId(schema?.markerId ?? 0);
    setIsMaterial(schema?.isMaterial ?? false);
    setStockBasedOption(
      schema?.budget?.stockBasedOption ?? StockBasedOption.NONE,
    );
    setStartEndReadingOption(
      schema?.budget?.startEndReadingOption ?? StartEndReadingOption.NONE,
    );
    setCostType(schema?.budget?.type ?? SchemaBudgetType.VARIABLE);
    setRate(schema?.budget?.rate ?? null);
    setUnit(schema?.budget?.unit ?? '');
  };

  useEffect(() => {
    resetState();
  }, [schema?.id]);

  const handleConfirm = () => {
    onSave({
      name,
      colorId,
      markerId,
      locationType,
      isMaterial,
      ...(isMaterial && {
        budget: {
          type: costType,
          stockBasedOption,
          startEndReadingOption,
          ...(costType !== SchemaBudgetType.VARIABLE && { unit }),
          ...(costType === SchemaBudgetType.FIXED && rate !== null && { rate }),
        },
      }),
    });

    onClose();
  };

  const handleStep = (value: number) => {
    const currentIndex = getStepIndex(activeStep, STEPS_ARRAY);
    const next = STEPS_ARRAY[currentIndex + value];

    if (next) {
      setActiveStep(next.value);
    }
  };

  const getContent = () => {
    switch (activeStep) {
      case ModalStep.BASIC_DETAILS:
        return <BasicDetailsTab />;
      case ModalStep.COST_TRACKING:
        return <CostTrackingTab />;
      case ModalStep.COST_AND_UNIT:
        return <CostAndUnitTab />;
      default:
        return null;
    }
  };

  const currentIndex = getStepIndex(activeStep, STEPS_ARRAY);
  const isLastStep = currentIndex === STEPS_ARRAY.length - 1;
  const showBack = activeStep !== ModalStep.BASIC_DETAILS;
  const showConfirmButton = isLastStep;

  const isDisabled = isCurrentStepDisabled({
    STEPS_ARRAY,
    activeStep,
    name,
    costType,
    rate,
    unit,
  });

  const rightArrowColor = isDisabled
    ? colors.neutral.gray
    : colors.neutral.white;

  const title = schema ? 'Edit Inventory Type' : 'Create Inventory Type';
  const confirmText = schema ? 'Save' : 'Create';

  const footer = (
    <div className={classes.footer}>
      {showBack ? (
        <Button onClick={() => handleStep(-1)}>
          <Icon>chevron_left</Icon>Back
        </Button>
      ) : (
        <div />
      )}
      <div>
        <Button onClick={onClose} style={styles.cancelButton}>
          Cancel
        </Button>
        {showConfirmButton ? (
          <Button
            color="primary"
            variant="contained"
            onClick={handleConfirm}
            disabled={isDisabled}
          >
            {confirmText}
          </Button>
        ) : (
          <Button
            color="primary"
            variant="contained"
            onClick={() => handleStep(1)}
            disabled={isDisabled}
          >
            Next<Icon color={rightArrowColor}>chevron_right</Icon>
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <CreateEditSchemaModalContext.Provider
      value={{
        activeStep,
        setActiveStep,
        STEPS_ARRAY,
        name,
        setName,
        locationType,
        setLocationType,
        colorId,
        setColorId,
        markerId,
        setMarkerId,
        isMaterial,
        setIsMaterial,
        stockBasedOption,
        setStockBasedOption,
        startEndReadingOption,
        setStartEndReadingOption,
        costType,
        setCostType,
        rate,
        setRate,
        unit,
        setUnit,
      }}
    >
      <Modal
        title={title}
        footer={footer}
        open={open}
        onExited={resetState}
        contentStyle={styles.modalContent}
        width="41rem"
        disableCloseOnOutsideClick
      >
        <div className={classes.modalContent}>
          <StepperSidebar />
          {getContent()}
        </div>
      </Modal>
    </CreateEditSchemaModalContext.Provider>
  );
};

export default CreateEditSchemaModal;
